/**
 * arbi quickstart — interactive wizard for new users
 *
 * Walks a real user from zero to a working CLI setup:
 *   1. Detect/confirm server URL
 *   2. Register or login
 *   3. Select or create a workspace
 *   4. Ready to go
 */

import { Command } from 'commander'
import { createArbiClient } from '@arbidocs/client'
import {
  getErrorMessage,
  performPasswordLogin,
  formatWorkspaceChoices,
  generateNewWorkspaceKey,
  workspaces as workspaceOps,
  projects as projectOps,
} from '@arbidocs/sdk'
import { success, error, ref, dim } from '../output.js'
import { store, updateConfig } from '../config.js'
import { promptInput, promptPassword, promptSelect, promptConfirm } from '../prompts.js'

/**
 * @inquirer/prompts throws an `ExitPromptError` when the user hits Ctrl+C.
 * We match by name to avoid importing the class (extra dep).
 */
function isExitPromptError(err: unknown): boolean {
  return err instanceof Error && err.name === 'ExitPromptError'
}

export function registerQuickstartCommand(program: Command): void {
  program
    .command('quickstart')
    .description('Interactive setup wizard — configure, register/login, and select a workspace')
    .argument('[url]', 'Server URL (auto-detected if omitted)')
    .action(async (url: string | undefined) => {
      try {
        await runQuickstart(url)
      } catch (err) {
        if (isExitPromptError(err)) {
          // User hit Ctrl+C — exit cleanly without dumping a Node stack trace.
          console.log('\nSetup cancelled.')
          process.exit(130)
        }
        error(`Quickstart failed: ${getErrorMessage(err)}`)
        process.exit(1)
      }
    })
}

async function runQuickstart(url: string | undefined): Promise<void> {
  console.log('\nWelcome to ARBI CLI setup!\n')

  // 1. Resolve server URL
  const { config, source } = store.resolveConfigWithFallbacks()
  if (url) {
    const domain = new URL(url).hostname
    config.baseUrl = url.replace(/\/$/, '')
    config.deploymentDomain = domain
    store.saveConfig(config)
  }

  const useDetected = await promptConfirm(`Use server ${config.baseUrl}?`, true)
  if (!useDetected) {
    const customUrl = await promptInput('Server URL')
    try {
      const domain = new URL(customUrl).hostname
      config.baseUrl = customUrl.replace(/\/$/, '')
      config.deploymentDomain = domain
      store.saveConfig(config)
    } catch {
      error('Invalid URL')
      process.exit(1)
    }
  } else if (source !== 'config') {
    store.saveConfig(config)
  }
  dim(`Server: ${config.baseUrl}`)

  // 2. Register or login
  const action = await promptSelect('Do you have an account?', [
    { name: 'Yes, log me in', value: 'login' },
    { name: 'No, register a new account', value: 'register' },
  ])

  const arbiClient = createArbiClient({
    baseUrl: config.baseUrl,
    deploymentDomain: config.deploymentDomain,
    credentials: 'omit',
  })
  await arbiClient.crypto.initSodium()

  let email: string
  let password: string

  if (action === 'register') {
    email = await promptInput('Email')
    password = await promptPassword('Password')
    const confirmPw = await promptPassword('Confirm password')
    if (password !== confirmPw) {
      error('Passwords do not match.')
      process.exit(1)
    }

    // Verification
    const codeMethod = await promptSelect('Verification method', [
      { name: 'I have an invitation code', value: 'code' },
      { name: 'Send me a verification email', value: 'email' },
    ])

    let verificationCode: string
    if (codeMethod === 'code') {
      verificationCode = await promptInput('Invitation code')
    } else {
      console.log('Sending verification email...')
      const verifyResponse = await arbiClient.fetch.POST('/v1/user/verify-email', {
        body: { email },
      })
      if (verifyResponse.error) {
        error(`Failed to send verification email: ${JSON.stringify(verifyResponse.error)}`)
        process.exit(1)
      }
      success('Verification email sent. Check your inbox.')
      verificationCode = await promptInput('Verification code')
    }

    try {
      const firstName = (await promptInput('First name', false)) || 'User'
      const lastName = (await promptInput('Last name', false)) || ''
      await arbiClient.auth.register({
        email,
        password,
        verificationCode,
        firstName,
        lastName,
      })
      success(`Registered as ${email}`)
    } catch (err) {
      if (isExitPromptError(err)) throw err
      error(`Registration failed: ${getErrorMessage(err)}`)
      process.exit(1)
    }
  } else {
    email = await promptInput('Email')
    password = await promptPassword('Password')
  }

  // 3. Login
  try {
    const { arbi, loginResult } = await performPasswordLogin(config, email, password, store)
    success(`Logged in as ${email}`)

    // 4. Select or create workspace
    const { data: workspaces } = await arbi.fetch.GET('/v1/user/workspaces')
    const wsList = workspaces || []
    const memberWorkspaces = wsList.filter((ws) => ws.users?.some((u) => u.user.email === email))

    // Get user's personal project for workspace creation
    const userProjects = await projectOps.listProjects(arbi)
    const defaultProjectExtId = userProjects[0]?.external_id
    if (!defaultProjectExtId) throw new Error('No projects found for user')

    let workspaceId: string
    let workspaceName: string

    if (memberWorkspaces.length === 0) {
      console.log('Creating your first workspace...')
      const encryptedKey = await generateNewWorkspaceKey(arbi, loginResult.serverSessionKey)
      const ws = await workspaceOps.createWorkspace(
        arbi,
        'My Workspace',
        encryptedKey,
        defaultProjectExtId
      )
      workspaceId = ws.external_id
      workspaceName = ws.name
    } else {
      const choices = [
        ...formatWorkspaceChoices(memberWorkspaces),
        { name: '+ Create new workspace', value: '__new__', description: '' },
      ]
      const selected = await promptSelect('Select workspace', choices)

      if (selected === '__new__') {
        const name = (await promptInput('Workspace name', false)) || 'My Workspace'
        const encKey = await generateNewWorkspaceKey(arbi, loginResult.serverSessionKey)
        const ws = await workspaceOps.createWorkspace(arbi, name, encKey, defaultProjectExtId)
        workspaceId = ws.external_id
        workspaceName = ws.name
      } else {
        workspaceId = selected
        workspaceName = memberWorkspaces.find((w) => w.external_id === selected)?.name || ''
      }
    }

    updateConfig({ selectedWorkspaceId: workspaceId })

    // 5. Summary
    console.log('')
    success('Setup complete!')
    console.log('')
    console.log(`  Server:    ${ref(config.baseUrl)}`)
    console.log(`  Account:   ${ref(email)}`)
    console.log(`  Workspace: ${workspaceName} (${ref(workspaceId)})`)
    console.log('')
    dim('Try: arbi ask "hello"')
  } catch (err) {
    if (isExitPromptError(err)) throw err
    error(`Login failed: ${getErrorMessage(err)}`)
    process.exit(1)
  }
}
